import { format } from 'date-fns';
import { ThumbsUp, ThumbsDown, Meh } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/* ============================================================================
 * The brand's verdict on a video it approved
 * ============================================================================
 * Brands no longer approve with one button — they rate the video: liked, okay,
 * or disliked. ALL THREE ARE APPROVALS. review_status is 'approved' in
 * every case, the hire completes, the payout gate clears, and the creator is
 * never told which rating it got. The rating is stored separately in
 * cr_video_submissions.brand_reaction (creator-app migration
 * 20260922000002_cr_video_submissions_brand_reaction.sql).
 *
 * That distinction is the whole reason this badge is worded the way it is. An
 * admin reading "Disliked" on a row could easily take it for a rejection or a
 * payout problem — it is neither. It is the only signal ops gets that a brand
 * accepted work it was unhappy with, which is what makes it worth surfacing:
 * a creator whose videos keep coming back "Disliked" is still being paid, and
 * nobody would otherwise know.
 *
 *   BrandReactionBadge    — one video's rating, in the hire detail video table.
 *   BrandSentimentSummary — the hire's tally, above that table, so the shape of
 *                           a creator's work reads without scanning rows.
 *
 * Both render NOTHING when there is no rating to show. NULL means nobody was
 * asked (BMC decided it, the 7-day window took it, or it predates the ratings),
 * and a dash on those rows would be noise.
 * ========================================================================== */

export type BrandReaction = 'like' | 'neutral' | 'dislike';

export const BRAND_REACTION_META: Record<BrandReaction, {
  /** On the row. */
  label: string;
  /** In the tooltip heading and the summary legend. */
  verdict: string;
  /** What this rating actually means, in a sentence an admin can act on. */
  blurb: string;
  badge: string;
  /** The icon's own tinted disc inside the pill. */
  dot: string;
  /** The summary bar's segment. */
  bar: string;
  legend: string;
  border: string;
}> = {
  like: {
    label: 'Liked',
    verdict: 'The brand liked this video',
    blurb: 'Approved and paid for, with no reservations.',
    badge: 'border-emerald-200/80 bg-gradient-to-b from-emerald-50 to-emerald-100/70 text-emerald-800',
    dot: 'bg-emerald-500/15 text-emerald-700',
    bar: 'bg-emerald-500',
    legend: 'text-emerald-700',
    border: 'border-emerald-200',
  },
  neutral: {
    label: 'Okay',
    verdict: 'The brand found this video acceptable',
    blurb: 'Approved and paid for — they just were not impressed.',
    badge: 'border-amber-200/80 bg-gradient-to-b from-amber-50 to-amber-100/70 text-amber-800',
    dot: 'bg-amber-500/15 text-amber-700',
    bar: 'bg-amber-500',
    legend: 'text-amber-700',
    border: 'border-amber-200',
  },
  dislike: {
    label: 'Disliked',
    verdict: 'The brand disliked this video',
    blurb: 'Still approved and still paid for — they chose not to send it back.',
    badge: 'border-rose-200/80 bg-gradient-to-b from-rose-50 to-rose-100/70 text-rose-800',
    dot: 'bg-rose-500/15 text-rose-700',
    bar: 'bg-rose-500',
    legend: 'text-rose-700',
    border: 'border-rose-200',
  },
};

export function isBrandReaction(value: unknown): value is BrandReaction {
  return value === 'like' || value === 'neutral' || value === 'dislike';
}

// The middle mark is a neutral face, not a thumb on its side: a rotated
// thumbs-up is not a gesture anyone makes, and at this size it read as a broken
// thumbs-up. Same three marks as the brand dashboard's own picker.
function ReactionIcon({ reaction, className = '' }: { reaction: BrandReaction; className?: string }) {
  const Icon = reaction === 'dislike' ? ThumbsDown : reaction === 'neutral' ? Meh : ThumbsUp;
  return <Icon className={className} />;
}

function dateLine(iso: string | null | undefined): string {
  if (!iso) return '';
  try { return format(new Date(iso), 'MMM d, yyyy'); } catch { return ''; }
}

type ReactionFields = {
  brand_reaction?: string | null;
  /** Why, on a dislike — the brand is required to say. Never shown to the
   *  creator, which is exactly why ops seeing it here matters. */
  brand_reaction_note?: string | null;
  review_status?: 'approved' | 'rejected' | null;
  reviewed_at?: string | null;
  reviewed_by?: string | null;
};

export function BrandReactionBadge({ video }: { video: ReactionFields | null | undefined }) {
  const reaction = video?.brand_reaction;
  if (!isBrandReaction(reaction)) return null;

  const meta = BRAND_REACTION_META[reaction];
  const note = reaction === 'dislike' ? video?.brand_reaction_note?.trim() : null;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Not a button — informational, in a table row. The negative margin
              widens the hover target without moving the badge off its line. */}
          <span
            className={`-m-0.5 inline-flex cursor-default items-center gap-1.5 rounded-full border py-0.5 pl-0.5 pr-2 text-[11px] font-semibold shadow-sm ring-1 ring-inset ring-white/70 ${meta.badge}`}
          >
            <span className={`flex h-4 w-4 items-center justify-center rounded-full ${meta.dot}`}>
              <ReactionIcon reaction={reaction} className="h-2.5 w-2.5 shrink-0" />
            </span>
            {meta.label}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className={`w-72 max-w-[80vw] overflow-hidden rounded-lg border p-0 shadow-lg ${meta.border}`}
        >
          <div className="space-y-1.5 px-3 py-2.5">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <ReactionIcon reaction={reaction} className="h-2.5 w-2.5 shrink-0" />
              Brand&rsquo;s verdict
            </span>
            <p className="text-xs font-medium leading-snug text-foreground">{meta.verdict}</p>
            {/* The line that stops a "Disliked" row being read as a problem. */}
            <p className="text-xs leading-relaxed text-muted-foreground">{meta.blurb}</p>
            {/* The brand's own words, quoted rather than paraphrased — this is
                the only place they surface at all, since the creator is never
                sent them. A dislike always has one (the dialog requires it),
                but rows predating that requirement do not. */}
            {note && (
              <p className="whitespace-pre-wrap break-words border-l-2 border-rose-300 bg-rose-50/50 px-2.5 py-1.5 text-xs leading-relaxed text-foreground">
                &ldquo;{note}&rdquo;
              </p>
            )}
            {(video?.reviewed_at || video?.reviewed_by) && (
              <p className="border-t pt-1.5 text-[10px] text-muted-foreground">
                {dateLine(video?.reviewed_at)}
                {video?.reviewed_by ? `${video?.reviewed_at ? ' · ' : ''}${video.reviewed_by}` : ''}
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/* ── The hire's tally ─────────────────────────────────────────────────────────
 * A proportional bar, not three numbers in a row: the question ops is actually
 * asking is "what does this creator's work look like to the brand", and a
 * shape answers that at a glance where counts have to be read and compared.
 *
 * Counts only videos the brand itself rated — the denominator is
 * deliberately NOT every video on the hire, because an admin decision and an
 * auto-approval are silences, not opinions, and folding them in would dilute
 * the one signal this exists to show. */
export function BrandSentimentSummary({ videos }: { videos: ReactionFields[] | null | undefined }) {
  const counts: Record<BrandReaction, number> = { like: 0, neutral: 0, dislike: 0 };
  for (const v of videos ?? []) {
    if (isBrandReaction(v?.brand_reaction)) counts[v.brand_reaction] += 1;
  }
  const rated = counts.like + counts.neutral + counts.dislike;
  if (rated === 0) return null;

  const order: BrandReaction[] = ['like', 'neutral', 'dislike'];

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex cursor-default items-center gap-2.5 rounded-full border bg-muted/40 py-1 pl-3 pr-3.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Brand rated
            </span>
            {/* The bar. One segment per rating the brand actually gave, widths
                proportional — a hire where everything was liked reads as one
                solid emerald stroke. */}
            <span className="flex h-1.5 w-20 overflow-hidden rounded-full bg-border">
              {order.map(k => counts[k] > 0 && (
                <span
                  key={k}
                  className={BRAND_REACTION_META[k].bar}
                  style={{ width: `${(counts[k] / rated) * 100}%` }}
                />
              ))}
            </span>
            <span className="flex items-center gap-2">
              {order.map(k => counts[k] > 0 && (
                <span key={k} className={`inline-flex items-center gap-1 text-[11px] font-semibold tabular-nums ${BRAND_REACTION_META[k].legend}`}>
                  <ReactionIcon reaction={k} className="h-2.5 w-2.5 shrink-0" />
                  {counts[k]}
                </span>
              ))}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="w-72 max-w-[80vw] p-0">
          <div className="space-y-1.5 px-3 py-2.5">
            <p className="text-xs font-semibold text-foreground">
              How the brand rated {rated} video{rated === 1 ? '' : 's'}
            </p>
            <div className="space-y-1">
              {order.map(k => (
                <div key={k} className="flex items-baseline justify-between gap-3 text-xs">
                  <span className={`inline-flex items-center gap-1.5 ${BRAND_REACTION_META[k].legend}`}>
                    <ReactionIcon reaction={k} className="h-2.5 w-2.5 shrink-0" />
                    {BRAND_REACTION_META[k].label}
                  </span>
                  <span className="tabular-nums text-muted-foreground">{counts[k]}</span>
                </div>
              ))}
            </div>
            <p className="border-t pt-1.5 text-[10px] leading-relaxed text-muted-foreground">
              Every rating here is an approval — the creator was paid for all {rated} and never sees
              the rating. Videos BMC decided, or that auto-approved after 7 days, are not counted.
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
